import { FC, useEffect } from "react";
import useCloudFunctionCall from "@/hooks/useCloudFunctionCall";
import { AuthenticationResponse } from "@/features/auth/types/authenticationTypes";
import { brand } from "../../../../../../../branchAndBrandSpecific/utils/brandVariables/brandVariables";
import { createToast } from "@/utils/toaster/createToast";
import { getPasswordRules } from "@/features/auth/helperFunctions/getPasswordRules";
import {
  Box,
  Button,
  Flex,
  Heading,
  Spinner,
  Text,
  TextField,
} from "@radix-ui/themes";
import PasswordRule from "@/features/auth/components/PasswordRule/PasswordRule";
import i18next from "i18next";

export const ChangePasswordResetPassword: FC<{
  accountCredentials: {
    email: string;
    password: string | undefined;
    passwordVerify: string | undefined;
  };
  setFieldValue: (targetName: string, value: any) => Promise<void>;
  isPasswordValid: boolean;
  passwordsMatch: boolean;
  code: {
    0: number;
    1: number;
    2: number;
    3: number;
    4: number;
    5: number;
  };
  handleReset: any;
}> = ({
  accountCredentials,
  setFieldValue,
  isPasswordValid,
  passwordsMatch,
  code,
  handleReset,
}) => {
  const [response, _loading, _error, authenticate] =
    useCloudFunctionCall<AuthenticationResponse>(
      "userAuthentication",
      undefined
    );
  const userCredentials = {
    password: accountCredentials.password,
    verifyPassword: accountCredentials.passwordVerify,
  };
  const passwordRules = getPasswordRules(userCredentials);

  const handleNextStep = async () => {
    let concatenatedString = "";

    Object.keys(code).forEach((key) => {
      const value = code[key];
      if (typeof value === "number") {
        concatenatedString += value.toString();
      }
    });

    await authenticate({
      method: "email",
      platform: brand.brandValue,
      action: "reset-password",
      newPassword: accountCredentials.password,
      emailAddress: accountCredentials.email,
      passwordResetCode: concatenatedString,
    });
  };

  useEffect(() => {
    if (
      code &&
      response &&
      accountCredentials.password &&
      accountCredentials.passwordVerify &&
      passwordsMatch
    ) {
      if (response.status === "password-reset-success") {
        createToast({
          type: "success",
          message: "Password successfully updated",
        });
        handleReset();
      } else {
        createToast({
          type: "info",
          message: response.payload,
        });
      }
    }
  }, [response]);

  return (
    <Flex direction="column" gap="3">
      <Heading as="h2" size="4">
        Enter your new password
      </Heading>
      <Flex direction="column" gap="3">
        {/* New password */}
        <Flex direction="column" gap="1">
          <Flex justify="between" align="end">
            <Text as="label" size="2">
              {i18next.t("auth.input.password.create.label")}
            </Text>
          </Flex>
          <TextField.Root
            placeholder={i18next.t("auth.input.password.create.placeholder")}
            size="3"
            type="password"
            value={accountCredentials.password}
            onChange={(e) => {
              setFieldValue("password", e.target.value);
            }}
          />
        </Flex>

        {/* Repeat new password */}
        {accountCredentials.password && isPasswordValid && (
          <Flex direction="column" gap="1">
            <Flex justify="between" align="end">
              <Text as="label" size="2">
                {i18next.t("auth.input.password.repeat.label")}
              </Text>
            </Flex>
            <TextField.Root
              placeholder={i18next.t("auth.input.password.repeat.placeholder")}
              size="3"
              type="password"
              value={accountCredentials.passwordVerify}
              onChange={(e) => {
                setFieldValue("passwordVerify", e.target.value);
              }}
            />
          </Flex>
        )}

        <Button
          disabled={!isPasswordValid || !passwordsMatch}
          variant="solid"
          size="3"
          onClick={async (e) => {
            handleNextStep();
          }}
        >
          {_loading ? <Spinner /> : "Reset password"}
        </Button>
      </Flex>

      <Box>
        <Text as="p" mb="2">
          {i18next.t("auth.create.password.headline")}:
        </Text>
        <Flex wrap="wrap" gapY="2">
          {passwordRules.map((passRule, index) => (
            <PasswordRule
              key={`passRule_${index}`}
              rule={passRule.rule}
              isValid={passRule.isValid}
            />
          ))}
        </Flex>
      </Box>
    </Flex>
  );
};
